import { memo, useRef, useState } from 'react';
import AnimationContainer from '@/components/animation-container';
import LottieContainer from '@/components/lottie-container';
import { EveryoneIsAskItemIconSvg, ReplaceEveryoneIsAskSvg } from '@/components/svg-icons/qa-records';
import { environment } from '@gaodun.com/gtools';
import { IQuestionOfOcrSearch } from '..';
import { getQaStore } from '@/store/qa';
import { getGlobalStore } from '@/store/global';
import { IEveryoneIsAskItemInOcr } from '@/types';
import { query_everyone_is_asks } from '@/apis/qa';
import { useDebounceFn } from 'ahooks';
import classNames from 'classnames';
import './index.scss';

const QuestionOfOcrSearch = ({
    similarity,
    content,
    questionId,
    littleQuestionId,
    hideReplaceOcrResultBtn,
    recordId,
    everyAsks,
}: Omit<IQuestionOfOcrSearch, 'type' | 'role'>) => {
    const pageInfo = useRef({ pageNum: 1, pageSize: 3 });
    const noNextPage = useRef(everyAsks.length < 3);
    const [animation, setAnimation] = useState(false);
    const [everyoneIsAsks, setEveryoneIsAsks] = useState<IEveryoneIsAskItemInOcr[]>(everyAsks);

    const handleViewAnalysis = () => {
        let url = `https://${environment.getApiEnv()}sub-tiku.gaodun.com/single/question/${questionId}?color=5262ff`;
        if (littleQuestionId) url += `&sid=${littleQuestionId}`;
        window.open(url);
    };

    const handleViewDetail = (askId: number) => {
        getGlobalStore()?.publish('QuestionDetailDrawer', { type: 'show', askId });
    };

    const handleReplaceOcrResult = () => {
        if (!getQaStore()?.validateCanSend()) return;
        const { qaRecords, setQaRecords } = getQaStore()!;
        const index = qaRecords.findIndex(item => item.type === 'questionOfOcrSearch');
        setQaRecords(oldRecords => oldRecords.slice(0, index));
        getGlobalStore()?.publish('DialogueArea', {
            type: 'replaceOcrResult',
            params: { recordType: 46, picAnswerNotQuestion: { recordId } },
        });
    };

    const { run: handleReplaceEveryoneIsAsk } = useDebounceFn(
        async () => {
            setAnimation(true);
            setTimeout(() => setAnimation(false), 300);
            if (noNextPage.current) {
                getGlobalStore()!.publish('Toast', { type: 'show', message: '没有更多了' });
                return;
            }
            pageInfo.current.pageNum++;
            const { result } = await query_everyone_is_asks({
                resourceType: 2,
                resourceId: littleQuestionId || questionId,
                ...pageInfo.current,
            });
            const { list = [] } = result || {};
            noNextPage.current = list.length < 3;
            if (list.length > 0) {
                setEveryoneIsAsks(list);
            } else {
                getGlobalStore()!.publish('Toast', { type: 'show', message: '没有更多了' });
            }
        },
        { wait: 500 }
    );

    return (
        <div className="respondent-container">
            <AnimationContainer className="question-of-ocr-search" type="enter_from_the_left">
                <div className="question-of-ocr-search__title">
                    <LottieContainer
                        className="question-of-ocr-search__title-icon"
                        type="starsTwinkle"
                        excuteType="autoPlayByInit"
                        width={22}
                        delay={1000}
                    />
                    <span>已为你找到这道题目</span>
                </div>
                <div className="question-of-ocr-search__relative-container">
                    {!hideReplaceOcrResultBtn && (
                        <div className="question-of-ocr-search__replace-ocr-result" onClick={handleReplaceOcrResult} />
                    )}
                    <div className="question-of-ocr-search__container">
                        <div className="question-of-ocr-search__similarity">相似度 {similarity}%</div>
                        <div className="question-of-ocr-search__content" dangerouslySetInnerHTML={{ __html: content }}></div>
                    </div>
                    <div className="question-of-ocr-search__view-analysis" onClick={handleViewAnalysis}>
                        查看答案&解析
                    </div>
                </div>
                {everyoneIsAsks.length > 0 && (
                    <div className="ocr-everyone-is-ask">
                        <div className="ocr-everyone-is-ask__header">
                            <span>大家都在问</span>
                            <div className="ocr-everyone-is-ask__replace" onClick={handleReplaceEveryoneIsAsk}>
                                <ReplaceEveryoneIsAskSvg
                                    className={classNames('ocr-everyone-is-ask__icon', animation && 'ocr-everyone-is-ask__icon--animation')}
                                />
                                <span>换一换</span>
                            </div>
                        </div>
                        {everyoneIsAsks.map(item => (
                            <div key={item.askId} className="ocr-everyone-is-ask__item" onClick={() => handleViewDetail(item.askId)}>
                                <EveryoneIsAskItemIconSvg className="ocr-everyone-is-ask__item-icon" />
                                <span className="ocr-everyone-is-ask__content">{item.content}</span>
                            </div>
                        ))}
                    </div>
                )}
            </AnimationContainer>
        </div>
    );
};

export default memo(QuestionOfOcrSearch);
